<template>
    <Modal id="merge-cart-modal"
           size="lg"
           icon="fa-code-merge"
           :title="modalTitleText">
        <div v-if="mergeError"
             class="alert alert-danger">
            <p>
                <i class="fa-regular fa-triangle-exclamation"></i>
                {{ l(mergeAlertStartText) }}
                <br />
                {{ l(mergeError) }}
            </p>
        </div>
        <div v-else
             class="alert alert-info">
            <p>
                <i class="fa-regular fa-copy"></i>
                {{ l(mergeAlertStartText) }}
                <br />
                {{ l('MERGE_CART.MERGE_QUESTION') }}
            </p>
        </div>

        <div class="comparison-cols">
            <div class="comparison-cols__col">
                <CartOverview :stashed-cart="existingStashedCart"
                              :preview-visible="showPreviews"
                              @preview-clicked="showLines()"></CartOverview>
                <OrderPreviewLines v-if="showPreviews"
                                   class="mt-1"
                                   :lines="existingStashedCart.currentCart.itemList"
                                   :show-line-prices="false" />
            </div>

            <p>
                <i class="fa-regular fa-plus"></i>
            </p>
            <div class="comparison-cols__col">
                <CartOverview :stashed-cart="newStashedCart"
                              :preview-visible="showPreviews"
                              @preview-clicked="showLines()"></CartOverview>
                <OrderPreviewLines v-if="showPreviews"
                                   class="mt-1"
                                   :lines="newStashedCart.currentCart.itemList"
                                   :show-line-prices="false" />
            </div>
        </div>

        <template v-slot:footer>
            <div class="modl-footer">
                <SquareBtn size="lg"
                           class="cancel-btn"
                           @click="cancel">
                    {{ l('BACK') }}
                </SquareBtn>

                <SquareBtn color="danger"
                           size="lg"
                           @click="doNotMerge">
                    {{ l(doNotMergeButtonText) }}
                </SquareBtn>

                <SquareBtn v-if="!mergeError"
                           color="success"
                           size="lg"
                           @click="merge">
                    {{ l(mergeButtonText) }}
                </SquareBtn>
            </div>
        </template>

        <p class="text-center mt-1 small text-muted">
            <i class="fa-solid fa-circle-exclamation"></i>
            {{ l(mergeDuplicateInfoText) }}
        </p>
    </Modal>
</template>

<script>
import CartOverview from '@/epos/vue/modals/common/CartOverview';
import Modal from '@/vue/components/Modals/Modal';
import OrderPreviewLines from '@/epos/vue/modals/OrderPreview/OrderPreviewLines';
import SquareBtn from '@/vue/components/SquareBtn';
import UserFacingError from '@/libraries/DigiTickets/Errors/UserFacingError';
import { canMergeCarts } from '@/lib/Cart/mergeCarts';
import { ModalPropsMixin } from '@/vue/mixins/ModalPropsMixin';

export default {
    name: 'MergeCartModal',

    components: {
        SquareBtn,
        Modal,
        CartOverview,
        OrderPreviewLines,
    },

    mixins: [
        ModalPropsMixin,
    ],

    props: {
        /**
         * @type {StashedCart}
         */
        newStashedCart: {
            type: Object,
            required: true,
        },

        /**
         * @type {StashedCart}
         */
        existingStashedCart: {
            type: Object,
            required: true,
        },

        /**
         * Is the is merge order+cart or stashedCart+cart?
         *
         * @type {boolean}
         */
        isKitchenOrderMerge: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            showPreviews: false,
        };
    },

    computed: {
        doNotMergeButtonText() {
            if (this.isKitchenOrderMerge) {
                return 'MERGE_KITCHEN_ORDER.NO_MERGE_BTN_TEXT';
            }
            return 'MERGE_CART.NO_MERGE_BTN_TEXT';
        },
        mergeAlertStartText() {
            if (this.isKitchenOrderMerge) {
                return 'MERGE_KITCHEN_ORDER.MERGE_ALERT_START';
            }
            return 'MERGE_CART.MERGE_ALERT_START';
        },
        mergeButtonText() {
            return 'MERGE_CART.MERGE_BTN_TEXT';
        },
        mergeDuplicateInfoText() {
            if (this.isKitchenOrderMerge) {
                return 'MERGE_KITCHEN_ORDER.MERGE_DUPLICATE_INFO';
            }
            return 'MERGE_CART.MERGE_DUPLICATE_INFO';
        },
        mergeError() {
            const result = canMergeCarts(
                this.existingStashedCart.currentCart,
                this.newStashedCart.currentCart,
                this.isKitchenOrderMerge ? 'MERGE_KITCHEN_ORDER' : 'MERGE_CART',
            );
            if (result instanceof UserFacingError) {
                return result.getLangKey();
            }

            return null;
        },
        modalTitleText() {
            if (this.isKitchenOrderMerge) {
                return 'MERGE_KITCHEN_ORDER.MODAL_TITLE';
            }
            return 'MERGE_CART.MODAL_TITLE';
        },
    },

    methods: {
        merge() {
            this.resolve(true);
        },

        doNotMerge() {
            this.resolve(false);
        },

        showLines() {
            this.showPreviews = !this.showPreviews;
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../less/variables";

#merge-cart-modal {
    .comparison-cols {
        .cart-overview {
            .cart-overview-title,
            .cart-overview-detail {
                justify-content: center;
            }
        }

        .cart-overview:not(:last-child) {
            margin-bottom: 0;
        }
    }

    @media (max-width: @mobile-max) {
        .comparison-cols {
            flex-direction: column;
            &__col {
                width: 100%;
            }
        }
    }
}
</style>
